import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Truck,
  Package,
  Search,
  CheckCircle2,
  Clock,
  MapPin,
  Calendar,
  AlertCircle,
  HelpCircle,
  ShieldCheck,
  Globe,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { trackOrderBackend, BACKEND_URL } from '../services/api';

const ShippingAndTracking = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [trackedOrder, setTrackedOrder] = useState(null);

  const [backendShipping, setBackendShipping] = useState({
    standard_shipping_fee: 15.00,
    free_shipping_threshold: 500.00
  });

  // Fetch Live Real Shipping Rates from Backend DB Settings
  React.useEffect(() => {
    let isMounted = true;
    const fetchRealRates = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings && isMounted) {
            setBackendShipping({
              standard_shipping_fee: Number(data.settings.standard_shipping_fee || data.settings.shipping_rate || 15.00),
              free_shipping_threshold: Number(data.settings.free_shipping_threshold || 500.00)
            });
          }
        }
      } catch (e) {
        console.warn("Could not load backend shipping settings:", e);
      }
    };
    fetchRealRates();
    return () => { isMounted = false; };
  }, []);

  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) {
      setErrorMsg('Please enter both Order/Tracking Number and Email Address.');
      return;
    }

    setLoading(true);
    setErrorMsg('');
    setTrackedOrder(null);

    const res = await trackOrderBackend(orderNumber, email);
    setLoading(false);

    if (res.success && res.order) {
      setTrackedOrder(res.order);
    } else {
      setErrorMsg(res.message || 'No matching order found. Please check your order details and try again.');
    }
  };

  const shippingRates = [
    {
      region: 'United Kingdom (Standard Tracked Courier)',
      time: '2 - 3 Business Days',
      cost: backendShipping.standard_shipping_fee === 0
        ? 'FREE'
        : `£${backendShipping.standard_shipping_fee.toFixed(2)} (FREE on orders over £${backendShipping.free_shipping_threshold.toFixed(2)})`
    }
  ];

  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#0f172a', padding: '40px 24px 80px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
        
        {/* Breadcrumbs */}
        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, marginBottom: '24px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: '#0f172a', fontWeight: 700 }}>Shipping & Tracking</span>
        </div>

        {/* HERO HEADER */}
        <div style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 48px' }}>
          <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '12px', display: 'block' }}>
            REAL-TIME ORDER STATUS
          </span>
          <h1 style={{ fontSize: '42px', fontWeight: 900, color: '#0f172a', lineHeight: 1.15, marginBottom: '16px', letterSpacing: '-0.02em' }}>
            Shipping & Order Tracking
          </h1>
          <p style={{ fontSize: '15px', color: '#475569', lineHeight: 1.6, margin: 0 }}>
            Enter your order number or tracking number along with your email address below to get real-time tracking updates.
          </p>
        </div>

        {/* ORDER TRACKER FORM CARD */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '36px', boxShadow: '0 8px 30px rgba(0,0,0,0.03)', maxWidth: '720px', margin: '0 auto 56px' }}>
          <form onSubmit={handleTrackSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Order Number or Tracking Number *
                </label>
                <input
                  type="text"
                  placeholder="e.g. ORD-178720 or GB123456789RM"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  style={{ width: '100%', background: '#fafbfc', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '14px 16px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', marginBottom: '8px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  placeholder="e.g. user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ width: '100%', background: '#fafbfc', border: '1px solid #cbd5e1', borderRadius: '12px', padding: '14px 16px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {errorMsg && (
              <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '10px', padding: '12px 16px', color: '#dc2626', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={16} />
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                background: '#dc2626',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '14px',
                fontWeight: 800,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justify: 'center',
                gap: '8px',
                boxShadow: '0 8px 20px rgba(220,38,38,0.25)',
                transition: 'all 0.2s ease'
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Looking Up Order...</span>
                </>
              ) : (
                <>
                  <Search size={18} />
                  <span>Track Order Status</span>
                </>
              )}
            </button>
          </form>

          {/* DYNAMIC LIVE ORDER STATUS RESULT CARD */}
          {trackedOrder && (
            <div style={{ marginTop: '36px', paddingTop: '32px', borderTop: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
                <div>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.1em', textTransform: 'uppercase' }}>ORDER DETAILS</span>
                  <h3 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '4px 0 0' }}>{trackedOrder.order_number}</h3>
                </div>
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '8px 18px', borderRadius: '9999px', fontSize: '13px', fontWeight: 900 }}>
                  Status: {trackedOrder.order_status}
                </div>
              </div>

              {/* Order Info Bar */}
              <div style={{ background: '#fafbfc', border: '1px solid #f1f5f9', borderRadius: '16px', padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>ORDER DATE</div>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginTop: '2px' }}>
                    {new Date(trackedOrder.created_at).toLocaleDateString()}
                  </div>
                </div>

                {trackedOrder.tracking_number && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>TRACKING NUMBER</div>
                    <div style={{ fontSize: '13px', fontWeight: 900, color: '#dc2626', marginTop: '2px' }}>
                      {trackedOrder.tracking_number}
                    </div>
                  </div>
                )}

                {trackedOrder.courier_partner && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>COURIER PARTNER</div>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginTop: '2px' }}>
                      {trackedOrder.courier_partner}
                    </div>
                  </div>
                )}

                <div>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700 }}>TOTAL AMOUNT</div>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: '#dc2626', marginTop: '2px' }}>
                    £{parseFloat(trackedOrder.total_amount).toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Status Timeline Progress */}
              <h4 style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a', marginBottom: '16px' }}>Status History</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                {(trackedOrder.status_history && trackedOrder.status_history.length > 0
                  ? trackedOrder.status_history
                  : [{ status: trackedOrder.order_status, comment: 'Order processing in warehouse', timestamp: trackedOrder.created_at }]
                ).map((hist, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#dc2626', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                      <CheckCircle2 size={16} />
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>{hist.status}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{hist.comment || 'Status updated'}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        {new Date(hist.timestamp || trackedOrder.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Items List */}
              {trackedOrder.items && Array.isArray(trackedOrder.items) && trackedOrder.items.length > 0 && (
                <div>
                  <h4 style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a', marginBottom: '12px' }}>Items in Shipment</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {trackedOrder.items.map((item, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfc', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '12px 16px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                          {item.name || item.title || 'InfraEdge Red Light Panel'} <span style={{ color: '#dc2626' }}>(x{item.quantity || 1})</span>
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>
                          £{parseFloat(item.price || 0).toFixed(2)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* DYNAMIC SHIPPING INFORMATION SECTION */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '36px', boxShadow: '0 4px 16px rgba(0,0,0,0.02)' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', marginBottom: '12px' }}>
            Shipping & Delivery Rates
          </h2>
          <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.6, marginBottom: '28px' }}>
            All InfraEdge Red Light Therapy panels are dispatched directly from our UK fulfillment center with trackable courier delivery.
          </p>

          <div style={{ border: '1px solid #e2e8f0', borderRadius: '16px', overflow: 'hidden', marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#f8fafc', padding: '16px 20px', fontWeight: 800, fontSize: '12.5px', color: '#0f172a', borderBottom: '1px solid #e2e8f0' }}>
              <div>Estimated Delivery</div>
              <div>Standard Shipping Cost</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '18px 20px', fontSize: '14px', color: '#475569' }}>
              <div style={{ fontWeight: 800, color: '#0f172a' }}>2 - 3 Business Days</div>
              <div style={{ fontWeight: 900, color: '#dc2626' }}>
                £{backendShipping.standard_shipping_fee.toFixed(2)}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '20px' }}>
            <div style={{ background: '#fafbfc', border: '1px solid #f1f5f9', borderRadius: '16px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', color: '#dc2626' }}>
                <Truck size={20} />
                <h4 style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Free Delivery Threshold</h4>
              </div>
              <p style={{ fontSize: '12.5px', color: '#64748b', lineHeight: 1.5, margin: 0 }}>
                Orders over <strong style={{ color: '#0f172a' }}>£{backendShipping.free_shipping_threshold.toFixed(2)}</strong> qualify for 100% Free Tracked Courier Shipping automatically at checkout.
              </p>
            </div>

            <div style={{ background: '#fafbfc', border: '1px solid #f1f5f9', borderRadius: '16px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', color: '#dc2626' }}>
                <ShieldCheck size={20} />
                <h4 style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Insured Courier Transport</h4>
              </div>
              <p style={{ fontSize: '12.5px', color: '#64748b', lineHeight: 1.5, margin: 0 }}>
                Every panel shipment is 100% insured against loss or damage during transport with signature upon delivery.
              </p>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default ShippingAndTracking;
